import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { SiteHeader } from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  ShieldCheck, ClipboardText, CurrencyDollar, CheckCircle, X, MagnifyingGlass,
  Warning, ClockCounterClockwise, Package, Database,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* =========================================================================
 *  Portfolio Triage Console — /assessor/portfolio
 *
 *  Admin-only. List, filter, review, quote, approve, reject, mark-fulfilled.
 *  Two-pane layout: list on the left, detail/action panel on the right.
 * ======================================================================= */

const STATUS_STYLE = {
  pending:      { tone: 'bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]', label: 'Pending' },
  under_review: { tone: 'bg-[#E0F2FE] text-[#075985] border-[#7DD3FC]', label: 'Under review' },
  quoted:       { tone: 'bg-[#EDE9FE] text-[#5B21B6] border-[#C4B5FD]', label: 'Quoted' },
  accepted:     { tone: 'bg-[#DEF7EC] text-[#03543F] border-[#31C48D]', label: 'Accepted' },
  approved:     { tone: 'bg-[#DEF7EC] text-[#03543F] border-[#31C48D]', label: 'Approved' },
  fulfilled:    { tone: 'bg-[#F3F4F6] text-[#111827] border-[#D1D5DB]', label: 'Fulfilled' },
  rejected:     { tone: 'bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]', label: 'Rejected' },
};

function StatusChip({ status }) {
  const m = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${m.tone}`}>{m.label}</span>;
}

function ActionPanel({ req, onAction, onClose }) {
  const [action, setAction] = useState(null);
  const [notes, setNotes] = useState('');
  const [quoteAmount, setQuoteAmount] = useState('');
  const [quoteCurrency, setQuoteCurrency] = useState('USD');
  const [licenseTerms, setLicenseTerms] = useState('12-month non-exclusive · single-org use · no redistribution · row-capped under signed-URL delivery.');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    setAction(null); setNotes(''); setQuoteAmount(''); setErr('');
  }, [req?.id]);

  const submit = async () => {
    setErr(''); setSaving(true);
    try {
      const payload = { action, notes };
      if (action === 'quote') {
        const amount = parseFloat(quoteAmount);
        if (!(amount >= 0)) { setErr('Quote amount required.'); setSaving(false); return; }
        payload.quote_amount = amount;
        payload.quote_currency = quoteCurrency;
        payload.license_terms = licenseTerms;
      }
      await axios.post(`${API_URL}/api/portfolio/admin/requests/${req.id}/review`, payload, { withCredentials: true });
      onAction();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  if (!req) return (
    <div className="card-flat p-6 bg-white text-center text-[#4B5563] text-sm">
      Pick a request to review.
    </div>
  );

  return (
    <div className="card-flat p-5 bg-white" data-testid="portfolio-detail-panel">
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Ref · <span className="text-[#111827] font-mono">{req.ref}</span></p>
          <h3 className="text-base font-bold text-[#111827] truncate">{req.organization}</h3>
          <p className="text-[12px] text-[#4B5563]">{req.contact_name} · <a className="hover:underline" href={`mailto:${req.contact_email}`}>{req.contact_email}</a>{req.contact_title ? ` · ${req.contact_title}` : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusChip status={req.status} />
          <button onClick={onClose} className="text-[#4B5563]"><X size={16} /></button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3 text-[11px]">
        <div className="bg-[#F9FAFB] p-2 rounded"><p className="text-[#6B7280] text-[10px] font-bold uppercase tracking-wider">Industry</p><p className="font-semibold">{req.industry}</p></div>
        <div className="bg-[#F9FAFB] p-2 rounded"><p className="text-[#6B7280] text-[10px] font-bold uppercase tracking-wider">Tier</p><p className="font-semibold capitalize">{req.tier}</p></div>
        <div className="bg-[#F9FAFB] p-2 rounded"><p className="text-[#6B7280] text-[10px] font-bold uppercase tracking-wider">Category</p><p className="font-semibold capitalize flex items-center gap-1">{req.portfolio_category === 'ingredient' ? <Database size={11} /> : <Package size={11} />}{req.portfolio_category}</p></div>
        <div className="bg-[#F9FAFB] p-2 rounded"><p className="text-[#6B7280] text-[10px] font-bold uppercase tracking-wider">Access</p><p className="font-semibold capitalize">{req.access_type.replace('_', ' ')}</p></div>
      </div>

      <div className="mb-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">Portfolios ({req.portfolio_types.length})</p>
        <div className="flex flex-wrap gap-1">
          {req.portfolio_types.map(p => <span key={p} className="text-[10px] font-mono px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50">{p}</span>)}
        </div>
      </div>
      <div className="mb-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">Jurisdictions ({req.jurisdictions.length})</p>
        <div className="flex flex-wrap gap-1">
          {req.jurisdictions.map(j => <span key={j} className="text-[10px] px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50">{j}</span>)}
        </div>
      </div>
      <div className="mb-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">Intended use</p>
        <p className="text-[12px] text-[#111827] bg-[#F9FAFB] rounded p-2 leading-relaxed">{req.intended_use}</p>
      </div>
      {(req.target_volume || req.timeline || req.additional_notes) && (
        <div className="mb-3 text-[11px] text-[#4B5563] space-y-1">
          {req.target_volume && <p><strong>Volume:</strong> {req.target_volume}</p>}
          {req.timeline && <p><strong>Timeline:</strong> {req.timeline}</p>}
          {req.additional_notes && <p><strong>Notes:</strong> {req.additional_notes}</p>}
        </div>
      )}

      {req.quote && (
        <div className="border border-[#C4B5FD] bg-[#F5F3FF] rounded p-3 mb-3 text-[12px]">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#5B21B6] mb-1 flex items-center gap-1"><CurrencyDollar size={11} weight="fill" /> Quote on file</p>
          <p className="font-bold text-[#5B21B6] text-base tabular-nums">{req.quote.currency} {req.quote.amount.toLocaleString()}</p>
          {req.quote.license_terms && <p className="text-[11px] text-[#4B5563] mt-1">{req.quote.license_terms}</p>}
          <p className="text-[10px] text-[#4B5563] mt-1">Quoted by {req.quote.quoted_by} · {new Date(req.quote.quoted_at).toLocaleString()}</p>
        </div>
      )}

      {/* Action selector */}
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-1.5">Action</p>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5 mb-3">
        {[
          { id: 'set_under_review', label: 'Under review', allowed: ['pending'] },
          { id: 'quote',           label: 'Quote',         allowed: ['pending', 'under_review', 'quoted'] },
          { id: 'approve',         label: 'Approve',       allowed: ['quoted', 'under_review', 'accepted'] },
          { id: 'mark_fulfilled',  label: 'Fulfilled',     allowed: ['approved'] },
          { id: 'reject',          label: 'Reject',        allowed: ['pending', 'under_review', 'quoted'] },
        ].map(a => {
          const enabled = a.allowed.includes(req.status);
          const active = action === a.id;
          return (
            <button
              key={a.id}
              onClick={() => enabled && setAction(a.id)}
              disabled={!enabled}
              className={`text-[11px] px-2 py-1.5 rounded border text-center transition-colors ${
                active ? 'border-[#FF4E6A] bg-[#FFE9EE] text-[#111827] font-bold' :
                enabled ? 'border-gray-200 bg-white text-[#4B5563] hover:border-gray-400' :
                'border-gray-100 bg-gray-50 text-[#9CA3AF] cursor-not-allowed'
              }`}
              data-testid={`portfolio-action-${a.id}`}
            >{a.label}</button>
          );
        })}
      </div>

      {action === 'quote' && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="col-span-2">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Amount</Label>
            <Input type="number" step="100" value={quoteAmount} onChange={(e) => setQuoteAmount(e.target.value)} className="mt-1 h-9 text-sm tabular-nums" placeholder="12500" data-testid="portfolio-quote-amount" />
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Currency</Label>
            <select value={quoteCurrency} onChange={(e) => setQuoteCurrency(e.target.value)} className="mt-1 w-full h-9 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="portfolio-quote-currency">
              {['USD', 'EUR', 'GBP'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="col-span-3">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">License terms</Label>
            <Textarea value={licenseTerms} onChange={(e) => setLicenseTerms(e.target.value)} rows={2} className="text-[11px] mt-1" data-testid="portfolio-license-terms" />
          </div>
        </div>
      )}

      {action && (
        <>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Internal notes</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="text-[12px] mt-1 mb-2" placeholder={action === 'reject' ? 'Rejection rationale (visible internally)' : 'Optional internal notes'} data-testid="portfolio-action-notes" />
          {err && <p className="text-[11px] text-[#9B1C1C] mb-2">{err}</p>}
          <Button onClick={submit} disabled={saving} className="h-9 rounded bg-[#FF4E6A] hover:bg-[#E63E5A] text-white text-sm font-semibold" data-testid="portfolio-action-submit">
            <CheckCircle size={13} className="mr-1.5" weight="fill" /> {saving ? 'Saving…' : `Confirm — ${action.replace('_', ' ')}`}
          </Button>
        </>
      )}

      {/* Audit trail */}
      {req.audit?.length > 0 && (
        <div className="mt-5 pt-4 border-t border-gray-100">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-1.5 flex items-center gap-1"><ClockCounterClockwise size={11} /> Audit ({req.audit.length})</p>
          <div className="space-y-1.5">
            {req.audit.slice().reverse().map((a, i) => (
              <div key={i} className="bg-[#F9FAFB] rounded p-2 text-[11px] text-[#4B5563]">
                <p><strong>{a.by}</strong> · <span className="font-mono">{a.action}</span> · {new Date(a.at).toLocaleString()}</p>
                {a.notes && <p className="italic mt-0.5">"{a.notes}"</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PortfolioTriagePage() {
  const [items, setItems] = useState([]);
  const [counters, setCounters] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (q) params.set('q', q);
      params.set('limit', '100');
      const r = await axios.get(`${API_URL}/api/portfolio/admin/requests?${params}`, { withCredentials: true });
      setItems(r.data.items || []);
      setCounters(r.data.counters || {});
      // Refresh selected
      if (selected) {
        const fresh = (r.data.items || []).find(x => x.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch (e) { /* keep */ }
    finally { setLoading(false); }
  }, [statusFilter, q, selected?.id]);

  useEffect(() => {
    const h = setTimeout(load, 250);
    return () => clearTimeout(h);
  }, [load]);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={22} className="text-[#FF4E6A]" weight="duotone" />
            <h1 className="text-2xl font-bold tracking-tight text-[#111827]">Portfolio Request Triage</h1>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] bg-[#F3F4F6] px-2 py-0.5 rounded ml-2">licensed access · admin only</span>
          </div>
          <p className="text-sm text-[#4B5563]">
            Review, scope, quote, and approve external partner requests for ingredient and packaging intelligence portfolios. Every action is audited.
          </p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <Link to="/assessor"><Button variant="outline" className="h-8 text-xs rounded" data-testid="portfolio-to-assessor">← Assessor Console</Button></Link>
            <Link to="/portfolio" target="_blank" rel="noreferrer"><Button variant="outline" className="h-8 text-xs rounded" data-testid="portfolio-public-link">View public portal ↗</Button></Link>
          </div>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 mb-4">
          {[
            { id: '',             label: 'All',          val: counters.total, tone: 'text-[#111827]' },
            { id: 'pending',      label: 'Pending',      val: counters.pending, tone: 'text-[#723B13]' },
            { id: 'under_review', label: 'Reviewing',    val: counters.under_review, tone: 'text-[#075985]' },
            { id: 'quoted',       label: 'Quoted',       val: counters.quoted, tone: 'text-[#5B21B6]' },
            { id: 'approved',     label: 'Approved',     val: counters.approved, tone: 'text-[#03543F]' },
            { id: 'fulfilled',    label: 'Fulfilled',    val: counters.fulfilled, tone: 'text-[#111827]' },
            { id: 'rejected',     label: 'Rejected',     val: counters.rejected, tone: 'text-[#9B1C1C]' },
          ].map(c => (
            <button key={c.id || 'all'} onClick={() => setStatusFilter(c.id)} className={`p-2 rounded border text-left ${statusFilter === c.id ? 'border-[#FF4E6A] bg-[#FFE9EE]' : 'border-gray-200 bg-white hover:border-gray-400'}`} data-testid={`portfolio-counter-${c.id || 'all'}`}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">{c.label}</p>
              <p className={`text-lg font-black tabular-nums ${c.tone}`}>{c.val ?? 0}</p>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* List */}
          <div className="lg:col-span-2 card-flat bg-white">
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search org / contact / ref…" className="h-8 text-sm pl-8" data-testid="portfolio-search" />
              </div>
            </div>
            {loading && items.length === 0 ? (
              <p className="text-sm text-[#4B5563] py-10 text-center">Loading…</p>
            ) : items.length === 0 ? (
              <p className="text-sm text-[#4B5563] py-10 text-center">No requests yet. Public portal is at <Link to="/portfolio" className="underline" target="_blank">/portfolio</Link>.</p>
            ) : (
              <div className="divide-y divide-gray-100 max-h-[640px] overflow-y-auto">
                {items.map(it => (
                  <button key={it.id} onClick={() => setSelected(it)} className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 ${selected?.id === it.id ? 'bg-[#FFE9EE]' : ''}`} data-testid={`portfolio-row-${it.id}`}>
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <p className="text-sm font-semibold text-[#111827] truncate">{it.organization}</p>
                      <StatusChip status={it.status} />
                    </div>
                    <p className="text-[11px] text-[#4B5563] truncate">
                      <ClipboardText size={10} weight="fill" className="inline mr-0.5" /> {it.ref} · <span className="capitalize">{it.portfolio_category}</span> · {it.portfolio_types.length} portfolio{it.portfolio_types.length === 1 ? '' : 's'}
                    </p>
                    <p className="text-[10px] text-[#6B7280] truncate">{it.contact_email} · {new Date(it.created_at).toLocaleDateString()}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Detail */}
          <div className="lg:col-span-3">
            <ActionPanel req={selected} onAction={() => load()} onClose={() => setSelected(null)} />
          </div>
        </div>
      </main>
    </div>
  );
}
